'use client';

import cartIcon from '@/assets/images/icons/icon-cart.png';
import profileIcon from '@/assets/images/icons/icon-profile.png';
import FlexibleRoundTextIcon from '@/components/ui/FlexibleRoundTextIcon/FlexibleRoundTextIcon';
import LoadingLink from '@/components/ui/LoadingLink/LoadingLink';
import RoundIcon from '@/components/ui/RoundIcon/RoundIcon';
import RoundTextIcon from '@/components/ui/RoundTextIcon/RoundTextIcon';
import { PRODUCT_NAME } from '@/constants';
import { useCurrentPathname } from '@/hooks/useCurrentPathname';
import { useCartInfoAtom, useGlobalLoadingAtom, useLoginCartCountAtom } from '@/state';
import { getCartCount } from '@/utils/cartCount';
import useIsMovingToOtherPath from '@/utils/useIsMovingToOtherPath';
import type { Route } from 'next';
import { useSession } from 'next-auth/react';
import { useRouter } from 'next/navigation';
import { useEffect } from 'react';
import logo from '/public/logo.png';
import styles from './CommonNavigationBar.module.scss';

function CommonNavigationBar({ navButtonDispFlg = true, cartCount }: { navButtonDispFlg: boolean; cartCount: number }) {
  const [localCartInfo] = useCartInfoAtom();
  const [loginCartCount, setLoginCartCount] = useLoginCartCountAtom();
  const router = useRouter();
  const session = useSession();
  const currentPathname = useCurrentPathname();
  const isMovingToOtherPath = useIsMovingToOtherPath();
  const [_, startGlobalLoading] = useGlobalLoadingAtom();

  // SSRで渡されたcartCountでatomを初期化
  useEffect(() => {
    if (cartCount > 0 && loginCartCount === 0) {
      setLoginCartCount(cartCount);
    }
  }, [cartCount]);

  const handleOnProfileClick = (e: React.MouseEvent<HTMLDivElement>) => {
    e.preventDefault();
    // 既にサインインページにいる場合は、何もしない（現在のURLを維持）
    if (currentPathname.startsWith('/auth/signin')) {
      return;
    }
    // ログイン済み：アカウントページへ
    if (session.data) {
      if (isMovingToOtherPath('/account')) {
        startGlobalLoading();
      }
      router.push('/account');
      return;
    }
    // 未ログイン：サインインページへ
    if (isMovingToOtherPath('/auth/signin')) {
      startGlobalLoading();
    }

    router.push(`/auth/signin?callbackUrl=${encodeURIComponent(currentPathname)}`);
  };

  const cartBadgeCount = getCartCount(!!session.data, loginCartCount, localCartInfo);

  return (
    <div className={styles.commonNavigationBar}>
      {/* ナビゲーションバー */}
      <nav className={styles.navigationBar}>
        <ul className={styles.navigationWrapper}>
          <li>
            <LoadingLink href={'/'}>
              <img src={logo.src} alt={PRODUCT_NAME} className={styles.logo} />
            </LoadingLink>
          </li>
          <li>
            <LoadingLink href={'/user-designs'}>
              <span>みんなのデザイン</span>
            </LoadingLink>
          </li>
          <li>
            <LoadingLink href={'/works?sort=created_at_desc'} data-testid='works-list-link'>
              <span>作品一覧</span>
            </LoadingLink>
          </li>
          <li>
            <LoadingLink href={'/product'} data-testid='product-list-link'>
              <span>アイテム一覧</span>
            </LoadingLink>
          </li>
          <li>
            <LoadingLink href={'/faq'} data-testid='faq-link'>
              <span>FAQ</span>
            </LoadingLink>
          </li>
        </ul>
      </nav>
      {/* ボタンエリア */}
      {navButtonDispFlg && (
        <div className={styles.iconContainer}>
          <div className={styles.profileIcon} onClick={handleOnProfileClick}>
            {session.data ? (
              <RoundIcon imgSrc={profileIcon.src} bgColor='white' size='large' />
            ) : (
              <FlexibleRoundTextIcon
                text='新規登録 / LOGIN'
                bgColor='white'
                size='xLarge'
                textColor='black'
                fontStyle='normal'
                fontWeight='bold'
              />
            )}
          </div>
          <div
            className={styles.profileIcon}
            onClick={() => {
              if (isMovingToOtherPath('/cart')) {
                startGlobalLoading();
              }
              router.push('/cart');
            }}
          >
            <RoundIcon imgSrc={cartIcon.src} bgColor='white' size='large' badgeCount={cartBadgeCount} />
          </div>
        </div>
      )}
    </div>
  );
}

export default CommonNavigationBar;
